import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useGoogleAuth } from '../GoogleAuthProvider';
import { getAccessToken } from '../auth/google';
import { DEFAULT_STATS } from '../shared/constants';

interface UseGoogleSheetsReturn {
  data: any[];
  stats: any;
  headers: string[];
  rows: any[][];
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  authenticate: () => Promise<boolean>;
  updateRecord: (identifier: string, columnName: string, value: string) => Promise<void>;
  updateMultipleRecords: (rowIndexes: number[], columnName: string, value: string) => Promise<void>;
  addRecord: (newRecord: any) => Promise<void>;
  refetch: () => Promise<void>;
}

export const useGoogleSheets = (sheetName: string): UseGoogleSheetsReturn => {
  const location = useLocation();
  const { isAuthed } = useGoogleAuth();
  const [data, setData] = useState<any[]>([]);
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<any[][]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Route to sheet mapping - determines which sheet should auto-refresh for each route
  const getActiveSheetForRoute = (pathname: string): string => {
    switch (pathname) {
      case '/app/dashboard':
        return 'dashboard';
      case '/app/pipeline':
        return 'pipeline-leads';
      case '/app/lead-gen/place-request':
      case '/app/lead-gen/requests':
        return 'lead-requests';
      case '/app/lead-gen/pre-qualified':
        return 'pre-qualified';
      case '/app/lead-gen/qualified':
        return 'qualified';
      case '/app/call-agent':
        return 'call-leads';
      case '/app/whatsapp-agent':
        return 'whatsapp-leads';
      case '/app/email-agent':
        return 'email-leads';
      case '/app/meetings':
        return 'meetings';
      default:
        return '';
    }
  };

  // Load data from cache immediately on initialization
  useEffect(() => {
    (async () => {
      const { googleSheetsService } = await import('../services/googleSheetsService');
      
      const cachedData = googleSheetsService.loadDataFromCache(sheetName);
      if (cachedData) {
        console.log(`[useGoogleSheets] Loading cached data for ${sheetName}`);
        
        if (sheetName === 'dashboard') {
          // For dashboard, cachedData contains the consolidated data
          setData(cachedData);
        } else if (cachedData.headers && cachedData.rows) {
          // For regular sheets with headers and rows
          setHeaders(cachedData.headers);
          setRows(cachedData.rows);
          const { transformSheetData } = await import('../utils/dataTransformers');
          const transformedData = transformSheetData(cachedData.rows, cachedData.headers, sheetName);
          setData(transformedData);
        } else if (Array.isArray(cachedData)) {
          // For simple data arrays
          setData(cachedData);
        }
        
        setIsLoading(false);
      }
    })();
  }, [sheetName]);

  const authenticate = async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      // Authentication is handled by GoogleSheetsProvider
      // Just check if we're authenticated
      const success = isAuthed;
      setIsAuthenticated(success);
      if (success) {
        await fetchData(true);
      }
      return success;
    } catch (err) {
      setError('Authentication failed');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const getSpreadsheetId = (sheetType: string): string => {
    // Use the service's internal method to get spreadsheet ID
    const configs = [
      { id: 'dashboard', spreadsheetId: '1P9bIGL0JXjYHF-4GHnniViYsgEM-Kux-4oVih5c1ImQ' },
      { id: 'pipeline-leads', spreadsheetId: '11hgHi7NWQGkLHQZWktK98FPP16QaniP6pmInW1UAc1M' },
      { id: 'lead-requests', spreadsheetId: '1P9bIGL0JXjYHF-4GHnniViYsgEM-Kux-4oVih5c1ImQ' },
      { id: 'pre-qualified', spreadsheetId: '1P9bIGL0JXjYHF-4GHnniViYsgEM-Kux-4oVih5c1ImQ' },
      { id: 'qualified', spreadsheetId: '1P9bIGL0JXjYHF-4GHnniViYsgEM-Kux-4oVih5c1ImQ' },
      { id: 'call-leads', spreadsheetId: '1ycMtZ44fHhkO7NUi2QJtGDMXJAuu9CWPl1Fv2BH55Dw' },
      { id: 'whatsapp-leads', spreadsheetId: '18QQjzM1LGR0AzIyicEPTqo3qCiSA4KyHeMTN4wD-y_E' },
      { id: 'email-leads', spreadsheetId: '13lv1kfIBbvTp21PH_Za3AXwv-JBj5uW4jHAVujZK-_A' },
      { id: 'meetings', spreadsheetId: '1vpivmQM6Q9U4lkXIQOGrPvZ9PEJAj4SS1VPdT2QRlBA' }
    ];
    
    const config = configs.find(c => c.id === sheetType);
    return config?.spreadsheetId || '';
  };

  const getSheetNameForType = (sheetType: string): string => {
    const configs = [
      { id: 'dashboard', sheetName: 'Dashboard Stats' },
      { id: 'pipeline-leads', sheetName: 'Sales_Pipeline' },
      { id: 'lead-requests', sheetName: 'Leads_Requests' },
      { id: 'pre-qualified', sheetName: 'Staging_TBQ' },
      { id: 'qualified', sheetName: 'MAIN_Ready_Leads' },
      { id: 'call-leads', sheetName: 'Leads_Calls' },
      { id: 'whatsapp-leads', sheetName: 'Leads_WA' },
      { id: 'email-leads', sheetName: 'Leads_Emails' },
      { id: 'meetings', sheetName: 'Booked_Meetings' }
    ];
    
    const config = configs.find(c => c.id === sheetType);
    return config?.sheetName || '';
  };

  const fetchData = async (showLoadingIndicator: boolean = true) => {
    try {
      if (showLoadingIndicator) {
        setIsLoading(true);
      }
      setError(null);

      if (!isAuthed) {
        console.log('[useGoogleSheets] Not authenticated');
        return;
      }

      // Lazy import both service and transformers
      const [{ googleSheetsService }, { transformSheetData }] = await Promise.all([
        import('../services/googleSheetsService'),
        import('../utils/dataTransformers')
      ]);

      if (sheetName === 'dashboard') {
        // Fetch data for all dashboard-related sheets
        const dashboardSheets = [
          'meetings',
          'qualified', 
          'call-leads',
          'whatsapp-leads',
          'email-leads',
          'pipeline-leads',
          'pre-qualified'
        ];
        
        console.log(`[useGoogleSheets][${sheetName}] Fetching dashboard data for sheets:`, dashboardSheets);
        const multipleSheetData = await googleSheetsService.readMultipleSheets(dashboardSheets);
        
        // Process and transform data for each sheet
        const consolidatedData: any = {};
        
        for (const [sheetType, rawData] of Object.entries(multipleSheetData)) {
          if (rawData && rawData.headers && rawData.rows) {
            const transformedData = transformSheetData(rawData.rows, rawData.headers, sheetType);
            consolidatedData[sheetType] = transformedData;
            
            // Log pipeline-leads data specifically for dashboard
            if (sheetType === 'pipeline-leads') {
              console.log('[useGoogleSheets][Dashboard Fetch] Transformed pipeline-leads data:', transformedData);
            }
          } else if (rawData) {
            consolidatedData[sheetType] = rawData;
          } else {
            consolidatedData[sheetType] = [];
          }
        }
        
        setData(consolidatedData);
        
        // Save consolidated data to cache
        googleSheetsService.saveDataToCache(sheetName, consolidatedData);
      } else {
        const spreadsheetId = getSpreadsheetId(sheetName);
        const actualSheetName = getSheetNameForType(sheetName);
        
        console.log(`[useGoogleSheets][${sheetName}] Fetching data for single sheet: ${actualSheetName}`);
        
        if (!spreadsheetId || !actualSheetName) {
          throw new Error(`No configuration found for sheet type: ${sheetName}`);
        }
        
        // Fetch regular data
        let sheetHeaders, sheetRows;
        if (sheetName === 'pipeline-leads') {
          ({ headers: sheetHeaders, rows: sheetRows } = await googleSheetsService.readSheetWithHeaders(spreadsheetId, actualSheetName, 'A:AJ'));
        } else {
          ({ headers: sheetHeaders, rows: sheetRows } = await googleSheetsService.readSheetWithHeaders(spreadsheetId, actualSheetName, 'A:Z'));
        }
        
        setHeaders(sheetHeaders);
        setRows(sheetRows);
        
        // Transform data based on sheet type
        const transformedData = transformSheetData(sheetRows, sheetHeaders, sheetName);
        setData(transformedData);
        
        // Save to cache
        googleSheetsService.saveDataToCache(sheetName, { headers: sheetHeaders, rows: sheetRows });
      }
      
      // Clear error and set authenticated on successful fetch
      setError(null);
      setIsAuthenticated(true);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Connection lost. Displaying last known data. Please reconnect.');
      setIsAuthenticated(false);
      // Don't clear data - keep the last known cached data visible
    } finally {
      if (showLoadingIndicator) {
        setIsLoading(false);
      }
    }
  };

  const updateRecord = async (rowIndex: number, columnName: string, value: string): Promise<void> => {
    try {
      setError(null);
      
      if (!isAuthed) {
        throw new Error('Not authenticated');
      }
      
      const { googleSheetsService } = await import('../services/googleSheetsService');
      
      // Find the column index
      const columnIndex = headers.findIndex(header => header === columnName);
      if (columnIndex === -1) {
        throw new Error(`Column ${columnName} not found`);
      }
      
      const spreadsheetId = getSpreadsheetId(sheetName);
      const actualSheetName = getSheetNameForType(sheetName);
      
      if (!spreadsheetId || !actualSheetName) {
        throw new Error(`No configuration found for sheet type: ${sheetName}`);
      }
      
      // Update the Google Sheet
      await googleSheetsService.updateCell(spreadsheetId, actualSheetName, rowIndex, columnIndex, value);
      
      // Update local state directly for immediate UI feedback
      setData(prevData => 
        prevData.map(record => 
          record._rowIndex === rowIndex 
            ? { ...record, [columnName]: value }
            : record
        )
      );
      
      // Re-fetch data to ensure synchronization with Google Sheets
      await fetchData(false);
      
    } catch (err) {
      console.error('Failed to update record:', err);
      setError('Failed to update record');
    }
  };

  const updateMultipleRecords = async (rowIndexes: number[], columnName: string, value: string): Promise<void> => {
    try {
      console.log('[useGoogleSheets] updateMultipleRecords called:', { rowIndexes, columnName, value });
      
      setError(null);
      
      if (!isAuthed) {
        throw new Error('Not authenticated');
      }
      
      const { googleSheetsService } = await import('../services/googleSheetsService');
      
      // Find the column index
      const columnIndex = headers.findIndex(header => header === columnName);
      if (columnIndex === -1) {
        console.error('[useGoogleSheets] Column not found for multiple update:', columnName);
        throw new Error(`Column ${columnName} not found`);
      }
      
      const spreadsheetId = getSpreadsheetId(sheetName);
      const actualSheetName = getSheetNameForType(sheetName);
      
      if (!spreadsheetId || !actualSheetName) {
        throw new Error(`No configuration found for sheet type: ${sheetName}`);
      }
      
      // Update each record
      for (const rowIndex of rowIndexes) {
        console.log('[useGoogleSheets] Updating row:', rowIndex);
        await googleSheetsService.updateCell(spreadsheetId, actualSheetName, rowIndex, columnIndex, value);
      }
      
      console.log('[useGoogleSheets] Multiple records updated successfully');
      
      // Update local state for immediate UI feedback
      setData(prevData => 
        prevData.map(record => 
          rowIndexes.includes(record._rowIndex) 
            ? { ...record, [columnName]: value }
            : record
        )
      );
      
    } catch (err) {
      console.error('[useGoogleSheets] Failed to update multiple records:', err);
      setError('Failed to update records');
      throw err; // Re-throw to allow components to handle the error
    }
  };

  const addRecord = async (newRecord: any): Promise<void> => {
    try {
      console.log('[useGoogleSheets] addRecord called with:', newRecord);
      
      if (!isAuthed) {
        throw new Error('Not authenticated');
      }
      
      const { googleSheetsService } = await import('../services/googleSheetsService');
      
      // Add to local state immediately for better UX
      setData(prevData => [...prevData, newRecord]);

      const spreadsheetId = getSpreadsheetId(sheetName);
      const actualSheetName = getSheetNameForType(sheetName);
      
      if (!spreadsheetId || !actualSheetName) {
        throw new Error(`No configuration found for sheet type: ${sheetName}`);
      }
      
      // Convert record to array format for Google Sheets
      const recordHeaders = Object.keys(newRecord);
      const values = recordHeaders.map(header => newRecord[header] || '');
      
      console.log('[useGoogleSheets] Prepared data for Google Sheets:', {
        spreadsheetId,
        actualSheetName,
        headers: recordHeaders,
        values
      });
      
      await googleSheetsService.appendToSheet(spreadsheetId, actualSheetName, [values]);
      
      console.log('[useGoogleSheets] Successfully added record to Google Sheets');
      
      // Refresh data to get the latest state
      await fetchData(false); // Don't show loading for immediate updates
      
    } catch (err) {
      console.error('[useGoogleSheets] Failed to add record:', err);
      setError('Failed to add record');
      // Revert local changes on error
      await fetchData(false);
    }
  };

  const refetch = async (): Promise<void> => {
    await fetchData(true);
  };

  useEffect(() => {
    console.log(`[useGoogleSheets][${sheetName}] Hook mounted/re-rendered for route: ${location.pathname}`);
    let intervalId: NodeJS.Timeout | null = null;
    let isFirstLoadSuccessful = false;
    
    // Check authentication status using the service
    const checkAuth = async () => {
      console.log(`[useGoogleSheets][${sheetName}] checkAuth running. isAuthed: ${isAuthed}, current path: ${location.pathname}`);
      try {
        if (isAuthed) {
          // Set up automatic refresh every 15 seconds (15000ms) only for the currently active page
          const activeSheetForCurrentRoute = getActiveSheetForRoute(location.pathname);
          
          if (sheetName === activeSheetForCurrentRoute) {
            console.log(`[useGoogleSheets][${sheetName}] Active sheet detected on route: ${location.pathname}. Fetching initial data...`);
            
            try {
              await fetchData(true);
              isFirstLoadSuccessful = true;
            } catch (error) {
              console.error(`[useGoogleSheets][${sheetName}] Initial data fetch failed:`, error);
              isFirstLoadSuccessful = false;
            }
            
            // Only set up auto-refresh if first load was successful
            if (isFirstLoadSuccessful) {
              // Set different refresh intervals based on sheet type
              const refreshInterval = sheetName === 'dashboard' ? 60000 : sheetName === 'pre-qualified' ? 7000 : 15000; // 1 minute for dashboard, 7 seconds for pre-qualified, 15 seconds for others
              console.log(`[useGoogleSheets][${sheetName}] Setting up auto-refresh every ${refreshInterval/1000} seconds.`);
            
              intervalId = setInterval(async () => {
                console.log(`[useGoogleSheets][${sheetName}] Auto-refreshing data...`);
                try {
                  await fetchData(false); // Don't show loading indicator for auto-refresh
                } catch (error) {
                  console.error(`[useGoogleSheets][${sheetName}] Auto-refresh failed:`, error);
                }
              }, refreshInterval);
            }
            
          } else {
            console.log(`[useGoogleSheets][${sheetName}] Skipping auto-refresh for inactive sheet. Current route: ${location.pathname}, Active sheet for route: ${activeSheetForCurrentRoute}`);
            // For inactive sheets, just set loading to false since they use cached data
            setIsLoading(false);
          }
        } else {
          console.log(`[useGoogleSheets][${sheetName}] Not authenticated. Setting isLoading to false.`);
          setIsLoading(false);
        }
      } catch (err) {
        console.error(`[useGoogleSheets][${sheetName}] Auth check failed:`, err);
        setIsLoading(false);
      }
    };

    checkAuth();
    
    // Always return cleanup function to clear interval if it was set
    return () => {
      console.log(`[useGoogleSheets][${sheetName}] Hook cleanup for route: ${location.pathname}`);
      if (intervalId) {
        console.log(`[useGoogleSheets][${sheetName}] Cleaning up auto-refresh interval.`);
        clearInterval(intervalId);
      }
    };
  }, [location.pathname, sheetName, isAuthed, getAccessToken]);

  return {
    data,
    stats,
    headers,
    rows,
    isLoading,
    error,
    isAuthenticated: isAuthed,
    authenticate,
    updateRecord,
    updateMultipleRecords,
    addRecord,
    refetch
  };
};